import React, { FC, ReactNode, useMemo } from "react";
import { AuthStore, UserStore } from "src/stores";
import styled, { css } from "styled-components";
import { Icon } from ".";
import { theme } from "../config/theme";
import { Badge, StyledBadge } from "./badge";
export interface IAvatarProps {
  square?: boolean;
  online?: boolean;
  initials?: string;
  image?: string;
  hoverInitials?: boolean;
  onClick?: () => void;
  size?: AvatarSizes;
  border?: boolean;
  context?: string;
  backgroundColor?: string;
  leaderBadge?: ReactNode;
  className?: string;
  authStore?: AuthStore;
  userStore?: UserStore;
}

type AvatarWrapperProps = {
  initials?: string;
  backgroundImage?: string;
  backgroundColor?: string;
  square: boolean;
  size: AvatarSizes;
  border?: boolean;
  className?: string;
  leaderBadge?: ReactNode;
  hoverInitials?: boolean;
  hasImage?: boolean;
};

type AvatarSizes = "default" | "input" | "small" | "medium" | "large" | "xlarge";

const avatarSizes = {
  small: css`
    width:     1.6rem;
    height:    1.6rem;
    font-size: ${theme.font.size.xxsmall};
  `,
  default: css`
    width:     1.8rem;
    height:    1.8rem;
    font-size: ${theme.font.size.xxsmall};
  `,
  input: css`
    width:     2.4rem;
    height:    2.4rem;
    font-size: ${theme.font.size.xxsmall};
  `,
  medium: css`
    width:     4rem;
    height:    4rem;
    font-size: ${theme.font.size.default};
  `,
  large: css`
    width:     4.5rem;
    height:    4.5rem;
    font-size: ${theme.font.size.large};
  `,
  xlarge: css`
    width:     6rem;
    height:    6rem;
    font-size: ${theme.font.size.large};
  `
};

const Initials = styled.div<IAvatarProps>`
`;

export const Avatar: FC<IAvatarProps> = React.memo(
  (props: IAvatarProps): JSX.Element => {

    const { initials, onClick, size, image, square, border, backgroundColor, leaderBadge, online, hoverInitials } = props;

    const memoizedImage = useMemo(() => image, [image]);

    return (
      <div className={'avatar-' + size}>
        <AvatarWrapper
          key={memoizedImage ? memoizedImage.length : 0}
          border={border}
          initials={initials}
          size={size}
          leaderBadge={leaderBadge}
          square={square}
          onClick={onClick}
          backgroundColor={backgroundColor}
          hasImage={!!memoizedImage}
          backgroundImage={`url(${memoizedImage})`}
          hoverInitials={true}
        >
          {online && (
            <StyledBadge
              top="0rem"
              right="-0.45rem"
              width="1.4rem"
              height="1.4rem"
              color={theme.color.status.online} />
          )}
          {leaderBadge &&
            <Badge top="-2.2rem" color={theme.color.primary7} right="0" content={<Icon size="xxsmall" iconId="people" />} />
          }
          {!(!!memoizedImage) && <Initials>{initials}</Initials>}
        </AvatarWrapper>
      </div>
    );
  });

const AvatarWrapper = styled.div<AvatarWrapperProps>`
  position:           relative;
  font-weight:         ${theme.font.weight.normal};
  background-image:    ${(p) => p.backgroundImage ? p.backgroundImage : 'initial'};
  background-size:     cover;
  background-color:    ${(p) => p.backgroundColor || theme.color.slate11};
  background-position: center;
  border:              ${(p) => (p.border ? `1px solid ${theme.color.white}` : "initial")};
  color:               white;
  display:             flex;
  transform:           scale(1.01); /* This is neccessary, otherwise the padding is off on the cardavatars with inital because of the same transform on the card */
  cursor:              pointer;
  border-radius:       ${(p) => (p.square ? "0%" : "50%")};
  text-align:          center;
  justify-content:     center;
  align-items:         center;
  
  ::before {
    display: none;
    width: 100%;
    font-weight: ${theme.font.weight.normal};
    text-transform: uppercase;
    content: "${(p) => (p.initials ? p.initials : "")}";
  }
  
  ${(props) => (props.size ? avatarSizes[props.size] : avatarSizes["small"])};
  &:hover {
    &::before {
      display: ${p =>( p.hoverInitials && p.hasImage) ? 'block' : p.hasImage ? 'none' : 'block'};
    }
    background-image: ${p => p.hoverInitials && p.hasImage ? 'none' : p.backgroundImage};
    ${Initials} {
      opacity: 0;
    }
    ${Initials} {
      display: none;
    }
  }

  svg {
    text {
      font-size:   ${(props) => (props.size ? avatarSizes[props.size] : avatarSizes["small"])};
      font-weight: ${theme.font.weight.bold};
    }
  }
`;
